import React, { useState, useEffect, useRef } from 'react'
import { inject, observer } from 'mobx-react'
import { isNil, tail, first } from 'lodash-es'
import { Text } from '@vx/text'
import { StateType } from '../state'
import { NOT_AVAILABLE, SHORT_COUNTRY_NAMES } from '../constants'
import { CHALLENGES_COLORS } from '../../../lib/constants'
import { CountryCell } from '../types'
import gsap from 'gsap'
import { formatPercentageUniversal, formatNumberRound } from '../../../lib/format-utils'

const displayPadding = 20
const DEBUG = false
const GRAY = 'var(--gray100)'

interface Props {
  state?: StateType
  margin: { top: number; left: number; right: number; bottom: number }
  radius: number
}

export const CountriesPack: React.FC<Props> = inject('state')(({ state, margin, radius }) => {
  const { countries, setSelectedCountry, countriesTopFive } = state.CountriesState
  const { challenge } = state.Circles
  const svgWidth = radius * 2 + margin.right + margin.left
  const svgHeight = radius * 2 + margin.top + margin.bottom

  const circlesContainer = useRef(null)
  useEffect(() => {
    const circlesToAnimate = Array.from(circlesContainer.current.querySelectorAll('g')) as Element[]
    const firstCircle = first(circlesToAnimate)
    const restCircles = tail(circlesToAnimate)
    gsap
      .timeline()
      .set(circlesToAnimate, {
        attr: { opacity: 0 },
      })
      .to(firstCircle, {
        delay: 0.1,
        duration: 0.3,
        attr: { opacity: 1 },
      })
      .to(restCircles, {
        delay: -0.2,
        attr: { opacity: 1 },
        stagger: { amount: 0.5, ease: 'power4.out' },
      })
  }, [challenge])

  return (
    <svg className="-voronoi-treemap" width={svgWidth} height={svgHeight}>
      <g
        className="-emissions-display"
        transform={`translate(${[radius + margin.left, margin.top - displayPadding]})`}
      >
        <CountryAmountLabel />
      </g>

      <rect
        className="-cells-background"
        fill="transparent"
        width={svgWidth}
        height={svgHeight}
        onClick={() => setSelectedCountry(null)}
      />
      <g
        ref={circlesContainer}
        className="-cells"
        transform={`translate(${[margin.left, margin.top]})`}
      >
        {countries.map((country) => (
          <CountryCircle
            key={`${country.data.country_code}-${country.data.country}`}
            country={country}
            showLabel={countriesTopFive.includes(country)}
          />
        ))}
      </g>
    </svg>
  )
})

interface CountryCircleProps {
  state?: StateType
  country: CountryCell
  showLabel: boolean
}

const CountryCircle: React.FC<CountryCircleProps> = inject('state')(
  observer(({ state, country, showLabel }) => {
    const { selectedCountry, setSelectedCountry } = state.CountriesState
    const {
      data: { color, country: countryName },
      x,
      y,
      r,
    } = country as any

    const [isHovered, setIsHovered] = useState(null)
    const isSomethingSelected = selectedCountry !== null
    const isSelected = country === selectedCountry
    const isDeselected = isSomethingSelected && !isSelected
    const shortCountryName = SHORT_COUNTRY_NAMES[countryName]

    return (
      <g>
        <circle
          className="-cell"
          fill={
            isDeselected ? GRAY : isSelected ? CHALLENGES_COLORS[state.Circles.challenge] : color
          }
          opacity={isHovered && !isSelected ? 0.7 : 1}
          cx={x}
          cy={y}
          r={r}
          onMouseEnter={() => {
            setIsHovered(true)
          }}
          onMouseLeave={() => {
            setIsHovered(false)
          }}
          onClick={() => (isSelected ? setSelectedCountry(null) : setSelectedCountry(country))}
        />
        {showLabel && (
          <>
            {DEBUG && (
              <rect x={x - r} y={y - 20} width={r * 2} height={40} fill="none" stroke="orange" />
            )}
            <Text
              x={x}
              y={y}
              width={r * 2}
              fontSize={10}
              fontWeight="200"
              textAnchor="middle"
              verticalAnchor="middle"
              opacity={isDeselected ? 0.1 : 1}
              style={{ pointerEvents: 'none' }}
            >
              {shortCountryName ?? countryName}
            </Text>
          </>
        )}
      </g>
    )
  })
)

interface CountryAmountLabelProps {
  state?: StateType
}
const CountryAmountLabel = inject('state')(
  observer(({ state }: CountryAmountLabelProps) => {
    const { selectedCountry, hasLulucf } = state.CountriesState
    if (isNil(selectedCountry)) return null

    const { emission_mmt_co2, emission_percentage } = selectedCountry.data
    const isNotAvailable = (emission_mmt_co2 as any) === 'NA'

    return (
      <text textAnchor="middle">
        {isNotAvailable ? (
          <tspan>{NOT_AVAILABLE.label}</tspan>
        ) : (
          <>
            <tspan fontWeight="bold">{formatPercentageUniversal(emission_percentage)}</tspan>{' '}
            <tspan>({formatNumberRound(emission_mmt_co2)} mmt CO₂)</tspan>
            {hasLulucf && <tspan> *</tspan>}
          </>
        )}
      </text>
    )
  })
)
